import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { refreshToken } from '@az/auth-ping';
import clearSessionWithInfo from './utils/clearSessionWithInfo';
import { deleteSSOSessions, updateSSOSessions, validateSSOSessions, getAuthHeaders } from './userSSOSessions';
import { DESKTOP, CT40, TC75, ANDROID } from './constants';

const axiosInstance = axios.create();
const retryAxiosInstance = axios.create();
let isRefreshRequestInProgress = false;
let refreshRequest: Promise<any> | null = null;
let isUpdateRequestInProgress = false;
let updateRequest: Promise<any> | null = null;

const handleSessionClear = async (message: string) => {
  try {
    await deleteSSOSessions();
  } catch (error) {
    console.error('Error during session cleanup:', error);
  } finally {
    localStorage.removeItem('updateSSOCalled');
    clearSessionWithInfo();
  }
  return Promise.reject(new Error(message));
};

function refreshTokenOnce(): Promise<void> {
  const refreshData = sessionStorage.getItem('refreshData');
  if (!refreshData) {
    throw new Error('Refresh Token data not available');
  }
  const refreshValues = JSON.parse(refreshData);
  if (isRefreshRequestInProgress) {
    return refreshRequest!;
  }
  isRefreshRequestInProgress = true;
  refreshRequest = refreshToken(refreshValues.AUTH_ROOT, refreshValues.REDIRECT_URI, refreshValues.CLIENT_ID).finally(
    () => {
      isRefreshRequestInProgress = false;
    }
  );
  return refreshRequest;
}

function updateSSOSessionsOnce(): Promise<void> {
  if (isUpdateRequestInProgress) {
    return updateRequest!;
  }
  isUpdateRequestInProgress = true;
  updateRequest = updateSSOSessions().finally(() => {
    isUpdateRequestInProgress = false;
  });
  return updateRequest;
}

const attemptTokenRefresh = async (): Promise<void> => {
  let refreshSucceeded = false;
  const refreshData = sessionStorage.getItem('refreshData');
  if (!refreshData) {
    throw new Error('Refresh Token data not available');
  }
  try {
    await refreshTokenOnce();
    refreshSucceeded = true;
  } catch (error) {
    throw new Error('Failed to Refresh Token');
  }
  if (refreshSucceeded) {
    await updateSSOSessionsOnce();
  }
};

const determineDeviceType = () => {
  const deviceInfo = navigator.userAgent;
  if (deviceInfo.includes(TC75)) {
    return TC75;
  }
  if (deviceInfo.includes(ANDROID)) {
    return CT40;
  }
  return DESKTOP;
};

if (process.env.NODE_ENV !== 'test') {
  const configureInterceptors = ({ instance, isRetry }: { instance: AxiosInstance; isRetry?: boolean }) => {
    instance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
        const deviceType = determineDeviceType();
        if (deviceType === CT40 || deviceType === TC75) {
          try {
            const ignoredEndpoints = ['v1/messages', 'v1/driver-logoff'];
            // Check if the request URL contains any of the ignored endpoints
            const shouldIgnore = ignoredEndpoints.some(endpoint => config.url?.includes(endpoint));
            if (shouldIgnore) {
              return config;
            }
            const validateResponse = await validateSSOSessions();
            if (validateResponse.status === 200) {
              return config;
            }
            if (validateResponse.status === 204) {
              localStorage.removeItem('updateSSOCalled');
              clearSessionWithInfo();
              return Promise.reject(new Error('Session validation failed with 204 status'));
            }
            return handleSessionClear('Unexpected response from validateSSOSessions');
          } catch (err) {
            const error = err as AxiosError;
            console.error('validateSSOSessions failed:', error);
            if (error.response) {
              console.log('API returned error response:', error.response);
              if (error.response.status === 401) {
                console.log('401 - Unauthorized, trying refresh...');
                try {
                  await attemptTokenRefresh();
                  const authHeaders = getAuthHeaders();
                  // eslint-disable-next-line no-param-reassign
                  Object.keys(authHeaders).forEach(key => {
                    // eslint-disable-next-line no-param-reassign
                    config.headers[key] = authHeaders[key as keyof typeof authHeaders];
                  });
                  return config;
                } catch (refreshError) {
                  return handleSessionClear('Token refresh failed.');
                }
              }
            }
            return config;
          }
        } else {
          return config;
        }
      },
      (error: unknown) => Promise.reject(error)
    );
    instance.interceptors.response.use(
      // no need to intercept a successful response
      response => response,
      error => {
        const originalRequest = error.config;
        if (error.response.status === 401) {
          if (!isRetry) {
            const refreshData = sessionStorage.getItem('refreshData');
            if (refreshData) {
              const refreshValues = JSON.parse(refreshData);
              return refreshToken(refreshValues.AUTH_ROOT, refreshValues.REDIRECT_URI, refreshValues.CLIENT_ID)
                .then(() => {
                  const loginDataStorage = sessionStorage.getItem('loginData');
                  if (loginDataStorage) {
                    const loginValues = JSON.parse(loginDataStorage);
                    originalRequest.headers.Authorization = `Bearer ${loginValues.access_token}`;
                  } else {
                    clearSessionWithInfo();
                  }
                })
                .catch(() => {
                  clearSessionWithInfo();
                })
                .then(() => retryAxiosInstance(originalRequest));
            }
          }
          return clearSessionWithInfo();
        }
        return Promise.reject(error);
      }
    );
  };
  configureInterceptors({ instance: axiosInstance });
  configureInterceptors({ instance: retryAxiosInstance, isRetry: true });
}

export default axiosInstance;
